import { get } from 'svelte/store';
import { getAppConfig } from '../../../common/config/app-config';
import { APP_CONSTANTS } from '../../../common/config/app-constants';
import { httpGetJSON, httpPostJSON } from '../../../common/services/ajax-services';
import { getStr } from '../../../common/util';
import { getCMSComponentsFromHybrisAsync } from '../../common/services/hybris';
import { uploadImages } from '../../common/services/imageUploadHelper';
import { showSnackbar } from '../../common/store/snackbar_store';
import {
  Category,
  CMSComponent,
  HomeMaintenanceCartEntry,
  mapCMSCategories,
  mapExistingServiceEntries,
  splitUids,
} from '../mapper/home-maintenance.mapper';
import {
  currentHomeMaintenanceServiceData,
  openHomeMaintenanceAtcDialog,
  openHomeMaintenancePopup,
  SavedService,
  serviceData,
  showCityUnavailableError,
} from '../store/home_maintenance_store';

const { encodedContextPath, CSRFToken, currency }: any = getAppConfig();

export async function getHomeMaintenanceCategoryData(categories: string): Promise<Category[]> {
  try {
    const categoryUids = splitUids(categories);

    const categoryRes = await getCMSComponentsFromHybrisAsync(categoryUids);
    const categoryComponents: CMSComponent[] = categoryRes?.component ?? [];
    /* sort by sequence */

    const sortedCategories = [...categoryComponents].sort(
      (a, b) => Number(a.sequence ?? Infinity) - Number(b.sequence ?? Infinity)
    );

    const allChildUids = sortedCategories.flatMap(c => splitUids(c.childCategories));

    const childRes = await getCMSComponentsFromHybrisAsync(allChildUids);
    const childComponents: CMSComponent[] = childRes?.component ?? [];

    return mapCMSCategories(sortedCategories, childComponents);
  } catch (error) {
    console.error('Failed to load home maintenance categories', error);
    showSnackbar('Unable to load services', 'error');
    return [];
  }
}

export const openHomeMaintenanceDialog = (categoryCode: string, serviceTitle: string) => async () => {
  try {
    window['ACC'].loader.showLoader();

    const relativeUrl = APP_CONSTANTS.GET_HOME_MAINTENANCE_CATEGORY.replace('{categoryCode}', categoryCode);
    const url = `${encodedContextPath}/${relativeUrl}`;

    const response = await httpGetJSON(url, {}, false);

    const existingMapped = mapExistingServiceEntries(response);

    serviceData.update(data => ({
      ...data,
      ...existingMapped,
    }));

    console.log('existingMapped', existingMapped);

    if (response?.cityUnavailable) {
      showCityUnavailableError.set(true);
    }
    currentHomeMaintenanceServiceData.set(response);
    openHomeMaintenancePopup.set(true);
    window['ACC']?.mixpanel?.hmsClicked(response?.nameEn);
    window['ACC']?.extragtm?.hmsClickedGTM(response?.nameEn, serviceTitle);
  } catch (error) {
    console.error('Dialog load failed:', error);
    showSnackbar('Failed to load data', 'error');
  } finally {
    window['ACC'].loader.hideLoader();
  }
};

export function buildAddToCartPayload(options?: { vipAddToCart?: boolean }) {
  try {
    const isVip = options?.vipAddToCart === true;

    if (isVip) {
      return { vipAddToCart: true };
    }

    const data = get(serviceData);

    const serviceEntries: HomeMaintenanceCartEntry[] = Object.values(data)
      .filter(service => {
        if (!service.isSaved) return false;

        // NEW service → always send
        if (!service.isExisting) return true;

        // EXISTING → send only if modified
        return service.isModified === true;
      })
      .map(service => {
        const entry: HomeMaintenanceCartEntry = {
          productCode: service.productCode,
          categoryCode: service.categoryCode,
          description: service.description,
          imageUrls: service.imageUrls,
        };

        if ('entryNumber' in service) {
          entry.entryNumber = service.entryNumber;
        }

        return entry;
      });

    return { serviceEntries };
  } catch (error) {
    console.error('buildAddToCartPayload error:', error);
    return { serviceEntries: [] };
  }
}

export async function addHomeMaintenanceToCart(options?: { vipAddToCart?: boolean; source?: string }) {
  const payload = buildAddToCartPayload(options);

  // prevent empty normal payload
  if (!options?.vipAddToCart && !payload?.serviceEntries?.length) {
    return null;
  }
  console.log(payload, 'payload');
  const url = `${encodedContextPath}/${APP_CONSTANTS.ADD_HOME_MAINTENANCE_TO_CART}`;

  try {
    window['ACC']?.loader?.showLoader();

    const response = await httpPostJSON(
      url,
      JSON.stringify(payload),
      {
        'Content-Type': 'application/json',
        CSRFToken,
      },
      true
    );

    if (response?.errorCode) {
      showSnackbar(getStr(response.errorCode), 'info');
      return null;
    }

    const mapped = mapHomeMaintenanceAtcResponse(response);

    if (mapped) {
      openHomeMaintenanceAtcDialog(mapped);
      document.querySelector(window['ACC'].minicart.selector.miniCartTotal).innerHTML = response?.cartModifications[0]
        ?.miniCartCount
        ? response?.cartModifications[0]?.miniCartCount
        : 0;
      if (options?.vipAddToCart) {
        window['ACC']?.mixpanel?.hmsVipAddedToCart(
          options?.source,
          `${response?.cartModifications[0]?.entry?.basePrice?.value} ${currency}`
        );
      } else {
        if (response?.cartModifications?.length) {
          window['ACC']?.mixpanel?.hmsAddedToCart(response?.cartModifications);
          window['ACC']?.extragtm?.hmsAddedToCartGTM(response?.cartModifications);
        }
      }
    }

    return mapped;
  } catch (error) {
    console.error('Add to cart failed:', error);
    showSnackbar('Something went wrong', 'error');
    return null;
  } finally {
    window['ACC']?.loader?.hideLoader();
  }
}

export function mapHomeMaintenanceAtcResponse(response) {
  if (!response?.cartModifications?.length) return null;

  const entries = response.cartModifications.map(m => m.entry);

  return {
    entries,
    cartTotal: response.cartTotal,
    serviceData: entries.map(e => e.extraServiceAppData).filter(Boolean),
  };
}

export function buildSnapshot(description: string, images: string[]) {
  return `${description}|${images.join('|')}`;
}

export function isDirty(
  description: string,
  originalDescription: string,
  previews: string[],
  originalImages: string[]
) {
  return buildSnapshot(description, previews) !== buildSnapshot(originalDescription, originalImages);
}

export function canSave(params: {
  description: string;
  previews: string[];
  originalDescription: string;
  originalImageUrls: string[];
  hasDesc: boolean;
  hasUpload: boolean;
}) {
  const dirty = isDirty(params.description, params.originalDescription, params.previews, params.originalImageUrls);

  const isDescriptionValid = !params.hasDesc || params.description.trim().length > 0;

  const isImageValid = !params.hasUpload || params.hasDesc || params.previews.length > 0;

  return dirty && isDescriptionValid && isImageValid;
}
export function updateService(productCode: string, updater: (prev: any) => any) {
  serviceData.update(data => ({
    ...data,
    [productCode]: updater(data[productCode]),
  }));
}

export function removeService(productCode: string) {
  serviceData.update(data => {
    const copy = { ...data };
    delete copy[productCode];
    return copy;
  });
}

export function mergeImages(existingImageUrls: string[], uploadedUrls: string[]) {
  return [...existingImageUrls, ...uploadedUrls];
}

/* ---------------------------------------
   SERVICE SHAPER
--------------------------------------- */

export function buildUpdatedService(params: {
  prev: any;
  description: string;
  imageUrls: string[];
  entryNumber?: string;
  isExisting?: boolean;
}) {
  return {
    ...params.prev,
    description: params.description,
    imageUrls: params.imageUrls,
    entryNumber: params.entryNumber ?? params.prev?.entryNumber,
    isExisting: params.isExisting ?? params.prev?.isExisting ?? false,
    isSaved: true,
    isModified: true,
  };
}

export function createServiceVM(params: { service: any; existingService?: SavedService }) {
  const { service, existingService } = params;

  const displayTypes = service?.displayType || [];

  const hasDesc = displayTypes.includes('DESC');
  const hasUpload = displayTypes.includes('UPLOAD');
  const requiresSave = hasDesc || hasUpload;

  return {
    hasDesc,
    hasUpload,
    requiresSave,

    validateSave(input: {
      description: string;
      previews: string[];
      originalDescription: string;
      originalImageUrls: string[];
    }) {
      return canSave({
        ...input,
        hasDesc,
        hasUpload,
      });
    },

    buildFinalImages(existing: string[], uploaded: string[]) {
      return mergeImages(existing, uploaded);
    },

    buildService(
      prev: SavedService,
      data: {
        description: string;
        imageUrls: string[];
      }
    ) {
      return buildUpdatedService({
        prev,
        description: data.description,
        imageUrls: data.imageUrls,
        entryNumber: existingService?.entryNumber,
        isExisting: existingService?.isExisting,
      });
    },
  };
}

export function useHomeMaintenanceService(service, existingService?: SavedService) {
  const vm = createServiceVM({ service, existingService });

  let description = '';
  let images: File[] = [];
  let previews: string[] = [];
  let existingImageUrls: string[] = [];

  let originalDescription = '';
  let originalImageUrls: string[] = [];

  let isSaved = false;

  function hydrate(existing: SavedService) {
    description = existing.description || '';
    existingImageUrls = existing.imageUrls || [];
    previews = [...existingImageUrls];

    originalDescription = description;
    originalImageUrls = [...existingImageUrls];

    isSaved = true;
  }

  async function save() {
    try {
      const uploadedUrls = vm.hasUpload ? await uploadImages(images) : [];

      const finalImageUrls = vm.buildFinalImages(existingImageUrls, uploadedUrls);

      const data = get(serviceData);

      serviceData.set({
        ...data,
        [service.productCode]: vm.buildService(data[service.productCode], {
          description,
          imageUrls: finalImageUrls,
        }),
      });

      originalDescription = description;
      originalImageUrls = [...finalImageUrls];

      images = [];
      existingImageUrls = [...finalImageUrls];
      previews = [...finalImageUrls];

      isSaved = true;
    } catch (error) {
      console.error('Save failed:', error);
      showSnackbar('Failed to save service', 'error');
    }
  }
  function edit() {
    isSaved = false;
    originalDescription = description;
    originalImageUrls = [...previews];
  }

  function removeImage(index: number) {
    previews = previews.filter((_, i) => i !== index);

    if (index < originalImageUrls.length) {
      existingImageUrls = existingImageUrls.filter((_, i) => i !== index);
    } else {
      const newIndex = index - originalImageUrls.length;
      images = images.filter((_, i) => i !== newIndex);
    }
  }

  function validate() {
    return vm.validateSave({
      description,
      previews,
      originalDescription,
      originalImageUrls,
    });
  }

  return {
    vm,
    get description() {
      return description;
    },
    set description(val: string) {
      description = val;
    },

    get previews() {
      return previews;
    },

    get isSaved() {
      return isSaved;
    },

    hydrate,
    save,
    edit,
    removeImage,
    validate,
  };
}

export function clearCityUnavailable() {
  currentHomeMaintenanceServiceData.update(data => ({
    ...data,
    cityUnavailable: false,
  }));
  console.log('clearCityUnavailable');
}

export function closeCityUnavailableError() {
  showCityUnavailableError.set(false);
}
